import { captureException } from '@sentry/vue';
import { getAuth, signOut as firebaseSignOut, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail as sendPasswordResetEmailByFirebase } from 'firebase/auth';

import { DISPLAY_NAME, stateFactory } from '@/utils/state';

export const useAuth = () => {
  const displayName = stateFactory.create<string>(DISPLAY_NAME, '');

  /**
   * ID トークンを返す。未ログインなら空文字。
   *
   * `getIdToken()` は期限内ならキャッシュを返し、期限切れなら更新してから返すため、呼び出し側は期限を気にしなくてよい。
   * トークンを文字列で持ち回さないのは、保持した時点で期限切れを検知できなくなるため。
   * `authStateReady()` を待つのは、永続化からの復元が終わるまで `currentUser` が null のため。
   */
  const getToken = async (): Promise<string> => {
    try {
      const auth = getAuth();
      await auth.authStateReady();
      return auth.currentUser ? await auth.currentUser.getIdToken() : '';
    } catch (e) {
      // 呼び出し元は onRequest でこれを呼ぶため、throw するとリクエストが送出されず
      // onResponseError のエラー通知も出ない。空文字を返して 401 の経路に載せる
      captureException(e);
      return '';
    }
  };

  /** ログイン処理 */
  const signIn = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
      hd: 'j-cat.co.jp',
    });
    provider.addScope('openid');
    provider.addScope('email');
    provider.addScope('profile');

    const userCredential = await signInWithPopup(auth, provider);
    displayName.value = userCredential.user.displayName || '';

    return await userCredential.user.getIdToken();
  };

  /** ログアウト処理 */
  const signOut = async () => {
    await firebaseSignOut(getAuth());
  };

  /** パスワードリセットメールを送信する */
  const sendPasswordResetEmail = async (email: string) => {
    await sendPasswordResetEmailByFirebase(getAuth(), email);
  };

  return {
    signIn,
    signOut,
    displayName,
    getToken,
    sendPasswordResetEmail,
  };
};
